export type TFunctionBezierCurve = (
  timeFraction: number, // 0 to 1
  revers?: boolean
) => number; // 0 to 1
/*
 * revers = false => time progress to draw progress
 * revers = true => draw progress to time progress
 *
 * example: easyInOut
 * easyInOut(1, false) => 1;
 * easyInOut(1, true) => 1;
 *
 * easyInOut(0, false) => 0;
 * easyInOut(0, true) => 0;
 *
 * easyInOut(0.3, false) => 0.108;
 * easyInOut(0.108, true) => 0.3;
 *
 * easyInOut(0.7, false) => 0.8919999999999999;
 * easyInOut(0.8919999999999999, true) => 0.7;
 */

export abstract class BezierCurve {
  static easyInOut(timeFraction: number, revers = false): number {
    if (!revers) {
      return timeFraction < 0.5
        ? 4 * timeFraction * timeFraction * timeFraction
        : (timeFraction - 1) * (2 * timeFraction - 2) * (2 * timeFraction - 2) + 1;
    } else {
      return timeFraction < 0.5 ? Math.cbrt(timeFraction / 4) : Math.cbrt((timeFraction - 1) / 4) + 1;
    }
  }
}

interface IInitAnimate {
  duration: number;
  timing: Exclude<keyof typeof BezierCurve, "prototype"> | TFunctionBezierCurve;
  draw: (progress: number) => void;
  startProgress?: number;
}

export class Animation {
  animationProcessing = false;
  requestID!: number;
  animationStartTime!: number | null;

  duration!: number;
  timing!: TFunctionBezierCurve;
  draw!: (progress: number) => void;

  redefineStartTime!: boolean;
  fixStartTime = 0;

  initAnimate({ duration, timing, draw, startProgress }: IInitAnimate): void {
    this.duration = duration;
    this.draw = draw;
    if (typeof timing === "string") {
      this.timing = BezierCurve[timing];
    } else {
      this.timing = timing;
    }

    if (!this.animationProcessing && startProgress) {
      this.fixStartTime = Math.ceil(this.timing(startProgress, true) * duration);
    }

    if (this.animationProcessing) {
      this.redefineStartTime = true;
      cancelAnimationFrame(this.requestID);
    }

    this.animationProcessing = true;

    //!this.animationProcessing && this.on.start && this.on.start();
    this.requestID = requestAnimationFrame(this.animate);
  }

  animate = (time: number): void => {
    if (!this.animationStartTime) {
      this.animationStartTime = time - this.fixStartTime;
      this.fixStartTime = 0;
    }

    if (this.redefineStartTime) {
      const pastTime = time - this.animationStartTime;
      this.animationStartTime = this.animationStartTime - (this.duration - pastTime - pastTime);
      this.redefineStartTime = false;
    }

    let timeFraction = Math.abs(time - this.animationStartTime) / this.duration;
    if (timeFraction > 1) timeFraction = 1;

    const progress = this.timing(timeFraction);
    //this.on.processing && this.on.processing();
    this.draw(progress);

    if (timeFraction < 1) {
      this.requestID = requestAnimationFrame(this.animate);
    } else {
      //this.on.end && this.on.end();
      this.animationStartTime = null;
      this.animationProcessing = false;
    }
  };

  cancelAnimate = (): void => {
    cancelAnimationFrame(this.requestID);
    this.animationStartTime = null;
    this.animationProcessing = false;
  };
}
